import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Button, ScrollTopButton } from '@indipia/ui-core';
import { photoApi } from '../api/photoApi';
import { SearchBar, type SearchParams } from '../components/SearchBar';
import { PhotoCard } from '../components/PhotoCard';
import type { PhotoNavItem, SearchConditionChip } from './PhotoDetailMain';
import { usePhotoNavStore } from '../store/photoNavStore';
import { usePhotoCommonStore } from '../store/photoCommonStore';
import { highlight } from '../components/Highlight';
import type { SearchGroupedResponse } from '../api/photoApi';
import { AssigneeBadges, useCompanyMembers, useDefaultMemberFilter, useMemberFilterState } from '@indipia/shared-features';
import { AssigneeFilterField } from '../components/AssigneeFilterField';
import type { AssignedMember } from '@indipia/shared-features';

const BASE = '/agents/photo-organizer';
const LIMIT = 20;
const SEARCH_STATE_KEY = 'photo-organizer-search-state';

interface SearchPhoto {
  id: number;
  takenAt: string | null;
  registeredAt: string | null;
  date: string;
  thumbUrl: string | null;
  tags: Array<{ tagType: string; tagValue: string }>;
  hasGpsError: boolean;
}

interface SearchSiteGroup {
  siteId: number | null;
  siteName: string;
  photoCount: number;
  photos: SearchPhoto[];
  chargeMembers?: AssignedMember[];
  managerMembers?: AssignedMember[];
}

function fmtPhotoDate(takenAt: string | null): string {
  if (!takenAt) return '';
  const d = new Date(takenAt);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}.${day}`;
}

// BE PhotoService.tagSortFirst와 동일 규칙 — 한글 시작 태그 우선, 그 외 UTF-16 코드유닛 순
function tagSortFirst(a: string, b: string): number {
  const aKo = a.length > 0 && a.charCodeAt(0) >= 0xac00 && a.charCodeAt(0) <= 0xd7a3;
  const bKo = b.length > 0 && b.charCodeAt(0) >= 0xac00 && b.charCodeAt(0) <= 0xd7a3;
  if (aKo !== bKo) return aKo ? -1 : 1;
  return a < b ? -1 : a > b ? 1 : 0;
}

function minTag(tags: SearchPhoto['tags']): string | null {
  let min: string | null = null;
  for (const t of tags) {
    if (!t.tagValue) continue;
    if (min === null || tagSortFirst(t.tagValue, min) < 0) min = t.tagValue;
  }
  return min;
}

// BE sortByTagMap과 동일 조건 — 태그 가나다순(한글 우선) 우선, 태그 없으면 등록일 내림차순
function compareByTagThenRegisteredAt(a: SearchPhoto, b: SearchPhoto): number {
  const ta = minTag(a.tags);
  const tb = minTag(b.tags);
  if (ta === null && tb === null) {
    const ra = a.registeredAt ?? '';
    const rb = b.registeredAt ?? '';
    if (ra === rb) return a.id - b.id;
    return ra > rb ? -1 : 1;
  }
  if (ta === null) return 1;
  if (tb === null) return -1;
  return tagSortFirst(ta, tb);
}

function mapApiGroups(data: SearchGroupedResponse | undefined): SearchSiteGroup[] {
  return (data?.sites ?? []).map(g => ({
    siteId: g.siteId ?? null,
    siteName: g.siteName ?? '미배정',
    photoCount: g.photoCount ?? 0,
    chargeMembers: g.chargeMembers ?? [],
    managerMembers: g.managerMembers ?? [],
    photos: (g.photos ?? []).map(p => ({
      id: p.photoId,
      takenAt: p.takenAt ?? null,
      registeredAt: p.registeredAt ?? null,
      date: fmtPhotoDate(p.takenAt ?? null),
      thumbUrl: p.thumbUrl ?? null,
      tags: (p.tags ?? []).map(t => ({ tagType: t.tagType, tagValue: t.tagValue })),
      hasGpsError: !!p.hasGpsError,
    })),
  }));
}

interface RestoredState {
  searchParams: SearchParams;
  groups: SearchSiteGroup[];
  totalCount: number;
  sortAsc: boolean;
  hasNext: boolean;
  nextOffset: number;
  collapsedGroups: (number | null)[];
  scrollTop: number;
  memberIds?: number[];
}

export function SearchMain() {
  const navigate = useNavigate();
  const setPhotoDetailCtx = usePhotoNavStore(s => s.setPhotoDetailCtx);
  const commonCodes = usePhotoCommonStore(s => s.commonCodes);
  const spaceTagSet = useMemo(
    () => new Set((commonCodes?.['A01_SPACE_TAG'] ?? []).map(c => c.codeName)),
    [commonCodes],
  );

  // 첫 렌더에 sessionStorage를 동기적으로 읽어 복원 (이후 API 재호출 방지)
  const [restoredState] = useState<RestoredState | null>(() => {
    const saved = sessionStorage.getItem(SEARCH_STATE_KEY);
    if (!saved) return null;
    try { return JSON.parse(saved) as RestoredState; }
    catch { return null; }
  });
  const skipFetchRef = useRef(restoredState !== null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [searchParams, setSearchParams] = useState<SearchParams | null>(restoredState?.searchParams ?? null);
  // 담당자·관리자 조건(2026-10-12 개선사항 D-7) — 기본값: 직원은 나, 사장님은 조건 없음(전체 현장)
  const { myId, defaultIds } = useDefaultMemberFilter();
  const { data: companyMembers = [] } = useCompanyMembers();
  // 기본값은 공용 훅(로그인 정보가 늦게 들어와도 직원=[나]/사장님=[]). 상세에서 돌아온 경우에만 저장해 둔 값을 우선한다.
  const [filterIds, setFilterIds] = useMemberFilterState();
  const [restoredMemberIds, setRestoredMemberIds] = useState<number[] | null>(restoredState?.memberIds ?? null);
  const memberIds = restoredMemberIds ?? filterIds;
  const setMemberIds = (ids: number[]) => { setRestoredMemberIds(null); setFilterIds(ids); };
  const memberIdsKey = memberIds.join(',');
  const [resetKey, setResetKey] = useState(0);
  const [sortAsc, setSortAsc] = useState(restoredState?.sortAsc ?? true);
  const [groups, setGroups] = useState<SearchSiteGroup[]>(restoredState?.groups ?? []);
  const [totalCount, setTotalCount] = useState(restoredState?.totalCount ?? 0);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [hasNext, setHasNext] = useState(restoredState?.hasNext ?? false);
  const [nextOffset, setNextOffset] = useState(restoredState?.nextOffset ?? 0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number | null>>(
    () => new Set<number | null>(restoredState?.collapsedGroups ?? [])
  );
  const bottomRef = useRef<HTMLDivElement>(null);

  const hasSearched = searchParams !== null;
  const keywords = searchParams?.keyword?.split(',').filter(Boolean) ?? [];

  // 마운트 후 sessionStorage 정리 + 스크롤 위치 복원
  useEffect(() => {
    sessionStorage.removeItem(SEARCH_STATE_KEY);
  }, []);

  useLayoutEffect(() => {
    if (restoredState?.scrollTop && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = restoredState.scrollTop;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 검색 파라미터 변경 시 초기 조회 (복원 직후는 skip)
  useEffect(() => {
    if (!searchParams) return;
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }

    const controller = new AbortController();
    const allKws = searchParams.keyword?.split(',').filter(Boolean) ?? [];
    const apiParams = {
      keyword:   allKws.filter(k => !spaceTagSet.has(k)).join(',') || undefined,
      space:     allKws.find(k => spaceTagSet.has(k)),
      startDate: searchParams.startDate || undefined,
      endDate:   searchParams.endDate   || undefined,
      memberIds: memberIds.length ? memberIds : undefined,
      offset: 0,
      limit:  LIMIT,
    };

    setLoading(true);
    setFetchError(false);
    setGroups([]);
    setHasNext(false);
    setNextOffset(0);

    photoApi.searchPhotos(apiParams, controller.signal)
      .then(res => {
        const data = res.data?.data;
        setGroups(mapApiGroups(data));
        setTotalCount(data?.totalCount ?? 0);
        const newOffset = (data?.siteOffset ?? 0) + (data?.siteCount ?? 0);
        setNextOffset(newOffset);
        setHasNext(newOffset < (data?.totalSiteCount ?? 0));
      })
      .catch(err => { if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') setFetchError(true); })
      .finally(() => setLoading(false));

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, memberIdsKey]);

  const loadMore = useCallback(() => {
    if (!searchParams || loadingMore || !hasNext) return;
    setLoadingMore(true);
    const moreKws = searchParams.keyword?.split(',').filter(Boolean) ?? [];
    photoApi.searchPhotos({
      keyword:   moreKws.filter(k => !spaceTagSet.has(k)).join(',') || undefined,
      space:     moreKws.find(k => spaceTagSet.has(k)),
      startDate: searchParams.startDate || undefined,
      endDate:   searchParams.endDate   || undefined,
      memberIds: memberIds.length ? memberIds : undefined,
      offset: nextOffset,
      limit:  LIMIT,
    })
      .then(res => {
        const data = res.data?.data;
        setGroups(prev => [...prev, ...mapApiGroups(data)]);
        setTotalCount(data?.totalCount ?? 0);
        const newOffset = (data?.siteOffset ?? 0) + (data?.siteCount ?? 0);
        setNextOffset(newOffset);
        setHasNext(newOffset < (data?.totalSiteCount ?? 0));
      })
      .catch(err => { if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') console.error(err); })
      .finally(() => setLoadingMore(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loadingMore, hasNext, nextOffset, memberIdsKey]);

  useEffect(() => {
    const el = bottomRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleSearch = (params: SearchParams) => setSearchParams(params);

  const reset = () => {
    sessionStorage.removeItem(SEARCH_STATE_KEY);
    setSearchParams(null);
    setGroups([]);
    setTotalCount(0);
    setHasNext(false);
    setNextOffset(0);
    setCollapsedGroups(new Set());
    setMemberIds(defaultIds);
    setResetKey(k => k + 1);
  };

  const toggleSort = () => setSortAsc(prev => !prev);

  const sortedGroups = groups.map(g => ({
    ...g,
    photos: [...g.photos].sort((a, b) => {
      const cmp = compareByTagThenRegisteredAt(a, b);
      return sortAsc ? cmp : -cmp;
    }),
  }));

  const buildSearchConditions = (): SearchConditionChip[] => {
    const conditions: SearchConditionChip[] = keywords.map(label => ({ type: 'keyword' as const, label }));
    if (searchParams?.startDate || searchParams?.endDate) {
      const fmt = (iso: string) => { const [, m, d] = iso.split('-'); return `${m}.${d}`; };
      let label = '';
      if (searchParams.startDate && searchParams.endDate) {
        const days = Math.round(
          (new Date(searchParams.endDate).getTime() - new Date(searchParams.startDate).getTime()) / 86400000
        );
        label = days === 30 ? '최근 30일' : `${fmt(searchParams.startDate)} ~ ${fmt(searchParams.endDate)}`;
      } else if (searchParams.startDate) {
        label = `${fmt(searchParams.startDate)} ~`;
      }
      if (label) conditions.push({ type: 'date', label });
    }
    return conditions;
  };

  const handlePhotoClick = (clickedId: number) => {
    const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;
    sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify({
      searchParams, groups, totalCount, sortAsc, hasNext, nextOffset,
      collapsedGroups: Array.from(collapsedGroups),
      scrollTop,
      memberIds,
    }));
    const allPhotos: PhotoNavItem[] = groups.flatMap(g =>
      g.photos.map(p => ({ photoId: p.id, thumbUrl: p.thumbUrl, originalUrl: null }))
    );
    const idx = Math.max(0, allPhotos.findIndex(p => p.photoId === clickedId));
    setPhotoDetailCtx({ photos: allPhotos, initialIndex: idx, searchConditions: buildSearchConditions() });
    navigate(`${BASE}/photo-detail`);
  };

  return (
    // 페이지 레벨 블록 간격 48 — 검색 조건 카드 ↔ 결과 섹션.
    // gap 스케일은 xxl(40)이 최대라 48은 범용 spacing 스케일에서 가져온다.
    <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '32px 80px 64px', background: 'var(--shell-bg)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-09)', minHeight: 0 }}>
      {/* 스크롤 맨 위로(D-09 §15.55) — 이 div 가 스크롤 소유자라 main 의 버튼이 반응하지 않는다. 직속 자식이라 스스로 이 div 를 찾는다 */}
      <ScrollTopButton />

        {/* 검색 조건 카드 */}
        <div style={{ background: 'var(--shell-surface)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', overflow: 'visible', boxShadow: '0 2px 14px color-mix(in srgb, var(--shell-on-surface) 5%, transparent)', flexShrink: 0 }}>
          <div style={{ padding: '18px 28px 16px', borderBottom: '1px solid var(--shell-border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-md)', background: 'var(--gt-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            </div>
            <span style={{ fontSize: 'var(--type-heading-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>검색 조건</span>
            <Button variant="secondary" size="sm" className="ml-auto" onClick={reset} style={{ background: 'var(--gt-subtle)' }}>초기화</Button>
          </div>
          <div style={{ padding: '20px 28px' }}>
            <SearchBar
              key={resetKey}
              onSearch={handleSearch}
              enableTagSearch
              initialValues={resetKey === 0 ? restoredState?.searchParams : undefined}
              afterSearch={
                /* 담당자·관리자 조건 — 키워드 검색창 오른쪽 같은 줄, 라벨+선택(D-7) */
                <AssigneeFilterField
                  members={companyMembers}
                  values={memberIds}
                  onChange={setMemberIds}
                  myId={myId}
                />
              }
            />
          </div>
        </div>

        {/* 결과 섹션 — 결과 헤더는 아래 목록의 머리말이므로 한 section 안에서 gap-sm 로 붙인다.
            바깥 spacing-09 는 페이지 레벨 블록(검색 조건 카드 ↔ 이 섹션) 간격으로만 남는다. */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-sm)' }}>

        {/* 결과 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>
            총 <span style={{ color: 'var(--color-info)' }}>{totalCount}장</span>
            {sortedGroups.length > 0 && <span style={{ color: 'var(--shell-on-surface-2)', fontWeight: 600, fontSize: 'var(--type-body-sm-size)', marginLeft: 6 }}>· {sortedGroups.length}개 현장</span>}
          </span>
          <Button variant="etc" size="sm" onClick={toggleSort}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="15" y2="12" /><line x1="3" y1="18" x2="9" y2="18" /></svg>
            <span>{sortAsc ? '정배열' : '역배열'}</span>
            <svg style={{ transform: sortAsc ? 'none' : 'rotate(180deg)', transition: 'transform .2s' }} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
          </Button>
        </div>

        {/* 오류 */}
        {fetchError && !loading && (
          <div style={{ background: 'var(--shell-surface)', border: '1.5px solid var(--color-error)', borderRadius: 'var(--radius-xl)', padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--color-error)' }}>검색 결과를 불러오지 못했습니다.</div>
            <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>잠시 후 다시 시도해 주세요.</div>
          </div>
        )}

        {/* 로딩 스켈레톤 */}
        {loading && groups.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-md)' }}>
            {[0, 1].map(i => (
              <div key={i} style={{ background: 'var(--shell-surface)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
                <div style={{ height: 48, background: 'var(--shell-skeleton)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, padding: '14px 18px' }}>
                  {[0, 1, 2].map(j => (
                    <div key={j} style={{ height: 140, borderRadius: 'var(--radius-lg)', background: 'var(--shell-skeleton)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 검색 전 안내 */}
        {!hasSearched && (
          <div style={{ background: 'var(--shell-surface)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-xl)', background: 'color-mix(in srgb, var(--color-info) 8%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface-2)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            </div>
            <div style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>키워드 또는 날짜를 입력하세요.</div>
            <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>검색 조건을 설정하면 결과가 표시됩니다.</div>
          </div>
        )}

        {/* 결과 없음 */}
        {hasSearched && !loading && sortedGroups.length === 0 && (
          <div style={{ background: 'var(--shell-surface)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 52, height: 52, borderRadius: 'var(--radius-xl)', background: 'color-mix(in srgb, var(--color-info) 8%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--shell-on-surface-2)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            </div>
            <div style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>검색 결과가 없습니다.</div>
            <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-hint)' }}>키워드나 날짜 범위를 변경해보세요.</div>
          </div>
        )}

        {/* 현장별 그룹 결과 */}
        {sortedGroups.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-md)', opacity: loading ? 0.5 : 1, transition: 'opacity .2s', pointerEvents: loading ? 'none' : 'auto' }}>
            {/* 안내 문구 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: 'color-mix(in srgb, var(--color-info) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--color-info) 15%, transparent)', borderRadius: 'var(--radius-md)' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-info)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--color-info)', fontWeight: 600, lineHeight: 1.5 }}>
                현장명을 클릭 시 현장 상세로 이동합니다. 우측의 접기 버튼으로 카드를 접을 수 있습니다.
              </span>
            </div>
            {sortedGroups.map(group => {
              const isCollapsed = collapsedGroups.has(group.siteId);
              const toggleCollapse = (e: React.MouseEvent) => {
                e.stopPropagation();
                setCollapsedGroups(prev => {
                  const next = new Set(prev);
                  next.has(group.siteId) ? next.delete(group.siteId) : next.add(group.siteId);
                  return next;
                });
              };
              return (
                <div key={group.siteId ?? 'unassigned'} style={{ background: 'var(--shell-surface)', border: '1.5px solid var(--shell-border)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
                  {/* 현장 그룹 헤더 */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => group.siteId !== null ? navigate(`${BASE}/site/${group.siteId}`) : navigate(`${BASE}/site-list`)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); group.siteId !== null ? navigate(`${BASE}/site/${group.siteId}`) : navigate(`${BASE}/site-list`); } }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: isCollapsed ? 'none' : '1px solid var(--shell-border)', background: 'var(--shell-surface)', cursor: 'pointer', transition: 'background .1s' }}
                    onMouseOver={e => (e.currentTarget.style.background = 'var(--shell-bg)')}
                    onMouseOut={e => (e.currentTarget.style.background = 'var(--shell-surface)')}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="var(--color-info)" stroke="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
                    <span style={{ fontSize: 'var(--type-body-md-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>{highlight(group.siteName, keywords)}</span>
                    {/* 담당자 · 관리자 — 묶음 머리글 현장명 옆(D-7). 배지 클릭은 머리글 클릭과 별개 */}
                    <span className="min-w-0 flex-1">
                      <AssigneeBadges
                        chargeMembers={group.chargeMembers}
                        managerMembers={group.managerMembers}
                        filterIds={memberIds}
                        myId={myId}
                      />
                    </span>
                    <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)' }}>총 {group.photoCount}장</span>
                    <button
                      onClick={e => { toggleCollapse(e); }}
                      aria-label={isCollapsed ? '펼치기' : '접기'}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 'var(--radius-sm)', marginLeft: 2, flexShrink: 0, cursor: 'pointer', background: 'transparent', border: 'none', padding: 0, fontFamily: 'inherit' }}
                      onMouseOver={e => (e.currentTarget.style.background = 'color-mix(in srgb, var(--shell-on-surface) 6%, transparent)')}
                      onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .2s' }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </div>
                  {/* 사진 그리드 */}
                  {!isCollapsed && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, padding: '14px 18px' }}>
                      {group.photos.map(photo => (
                        <PhotoCard
                          key={photo.id}
                          photoId={photo.id}
                          thumbUrl={photo.thumbUrl}
                          takenAt={photo.takenAt}
                          registeredAt={photo.registeredAt}
                          date={photo.date}
                          tags={photo.tags.map(t => t.tagValue)}
                          showCheckbox={false}
                          showDetailHint
                          tagAreaDefaultCursor
                          thumbAreaDefaultCursor
                          onToggle={() => handlePhotoClick(photo.id)}
                          onViewPhoto={() => handlePhotoClick(photo.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        </section>

        {/* 무한 스크롤 sentinel */}
        <div ref={bottomRef} style={{ height: 1 }} />
        {loadingMore && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0', color: 'var(--shell-hint)', fontSize: 'var(--type-body-sm-size)' }}>
            불러오는 중...
          </div>
        )}

      </div>
  );
}
